// Head — face (frontal) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.3). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 200 260, centerline x = 100.
// A front-on face: forehead y ~40-90, brow / eyes y ~100-120, cheeks / maxilla
// y ~125-155, jaw line y ~170-205, neck below. Ears sit at x ~36 / x ~164.
//
// Both this panel and regionsTopDown.ts feed `headache` events, not `symptom`
// events (D-PAIN-2). Every region carries a `headacheLocation` (nearest
// HEADACHE_LOCATIONS value) so Session 6 can route the saved event without a
// lookup table. `headacheLocation` is optional on PainRegion and set ONLY in
// these two files.
//
// Coverage (Task 3.3): TMJ (paired), the frontal and maxillary sinus regions,
// the temples, the orbital regions, the jaw line, a forehead region for plain
// frontal headache, an occiput reference at the crown, and a `spine-c` marker
// at the neck for cervicogenic / neck-base headache.

import type { HeadacheLocation, PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const FACE_VIEWBOX = "0 0 200 260";

/** A closed rounded-rectangle outline path. */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

export const FACE_OUTLINE: string[] = [
  ellipsePath(100, 120, 60, 94), // face / cranium
  ellipsePath(38, 126, 10, 18), // left ear
  ellipsePath(162, 126, 10, 18), // right ear
  box(80, 204, 120, 244, 8), // neck
  ellipsePath(46, 246, 42, 12), // left shoulder (context)
  ellipsePath(154, 246, 42, 12), // right shoulder (context)
];

/** One region. `kind` is read from PAIN_STRUCTURE_META so it can never disagree
 *  with the taxonomy (validator rule S7); `side` is taken from the id suffix. */
function reg(
  id: string,
  structure: PainStructure,
  headacheLocation: HeadacheLocation,
  d: string,
): PainRegion {
  return {
    id,
    structure,
    kind: PAIN_STRUCTURE_META[structure].kind,
    side: id.endsWith("-l") ? "left" : id.endsWith("-r") ? "right" : "center",
    headacheLocation,
    d,
  };
}

export const FACE_STRUCTURES: PainRegion[] = [
  // ── forehead / plain frontal ─────────────────────────────────────────────
  reg("tissue-skin-forehead-c", "tissue-skin", "frontal", ellipsePath(100, 62, 30, 22)),

  // ── frontal sinuses (just above the brow) ────────────────────────────────
  reg("tissue-skin-frontal-sinus-l", "tissue-skin", "sinuses", ellipsePath(84, 90, 12, 9)),
  reg("tissue-skin-frontal-sinus-r", "tissue-skin", "sinuses", ellipsePath(116, 90, 12, 9)),

  // ── temples ─────────────────────────────────────────────────────────────
  reg("tissue-skin-temple-l", "tissue-skin", "temporal-left", ellipsePath(52, 96, 9, 15)),
  reg("tissue-skin-temple-r", "tissue-skin", "temporal-right", ellipsePath(148, 96, 9, 15)),

  // ── orbital regions (around / behind the eye) ────────────────────────────
  reg("tissue-skin-orbital-l", "tissue-skin", "behind-eye-left", ellipsePath(78, 110, 14, 9)),
  reg("tissue-skin-orbital-r", "tissue-skin", "behind-eye-right", ellipsePath(122, 110, 14, 9)),

  // ── maxillary sinuses (cheeks, below the eyes) ───────────────────────────
  reg("tissue-skin-maxillary-sinus-l", "tissue-skin", "sinuses", ellipsePath(76, 140, 14, 12)),
  reg("tissue-skin-maxillary-sinus-r", "tissue-skin", "sinuses", ellipsePath(124, 140, 14, 12)),

  // ── temporomandibular joints (just in front of each ear) ─────────────────
  reg("joint-tmj-l", "joint-tmj", "temporal-left", ellipsePath(58, 132, 7, 9)),
  reg("joint-tmj-r", "joint-tmj", "temporal-right", ellipsePath(142, 132, 7, 9)),

  // ── jaw line (angle / body of the mandible) ──────────────────────────────
  reg("tissue-skin-jaw-l", "tissue-skin", "temporal-left", ellipsePath(66, 188, 12, 17)),
  reg("tissue-skin-jaw-r", "tissue-skin", "temporal-right", ellipsePath(134, 188, 12, 17)),

  // ── occiput reference (crown / back of head) ─────────────────────────────
  reg("tissue-skin-occiput-c", "tissue-skin", "occipital", ellipsePath(100, 34, 22, 12)),

  // ── neck / cervical reference ───────────────────────────────────────────
  reg("spine-c-c", "spine-c", "neck-base", ellipsePath(100, 226, 14, 12)),
];
